'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';

const stats = [
  {
    value: '$200M+',
    label: 'Capital Raised / Closed',
    desc: 'Across supported fundraising activities and networks',
  },
  {
    value: 'Global',
    label: 'Investor Network',
    desc: 'Relationships across investors, funds, family offices and syndicates',
  },
  {
    value: 'Multi-Stage',
    label: 'Founder Experience',
    desc: 'From early-stage to significant growth capital',
  },
  {
    value: 'Both Sides',
    label: 'Investor Experience',
    desc: 'Understanding mandates, preferences and decision-making',
  },
];

const ecosystems = [
  'Startup Ecosystems',
  'Accelerator Programs',
  'Founder Advisory',
  'Capital Raising',
  'Investor Relationships',
  'Syndicates',
  'Fund Structures',
  'Venture Opportunities',
  'International Networks',
];

export default function TrackRecordSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="track-record"
      className="py-24 px-6 border-t border-primary/10 bg-secondary/20"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <p className="section-label scroll-fade mb-5">Track Record</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1">
              Experience built through years of working with{' '}
              <span className="font-serif italic text-primary">founders and capital.</span>
            </h2>
          </div>
          <div className="scroll-fade scroll-fade-delay-2">
            <div className="flex flex-wrap gap-2">
              {ecosystems?.map((eco) => (
                <span
                  key={eco}
                  className="text-xs px-3 py-1.5 rounded-full border border-primary/15 text-muted-foreground"
                >
                  {eco}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        {/* BENTO AUDIT: 4 stats, 2x2 */}
        {/* Row 1: [col-1: Capital cs-1] [col-2: Network cs-1] */}
        {/* Row 2: [col-1: Founder cs-1] [col-2: Investor cs-1] */}
        {/* Placed 4/4 ✓ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {stats?.map((stat, i) => (
            <div
              key={stat?.label}
              className={`bento-card rounded-2xl p-8 text-center scroll-fade ${i === 0 ? 'lg:border-primary/30 lg:bg-primary/5' : ''}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="text-3xl font-semibold text-foreground tracking-tight mb-2">
                {stat?.value}
              </div>
              <div className="text-xs font-semibold uppercase tracking-widest text-primary mb-2">
                {stat?.label}
              </div>
              <p className="text-xs text-muted-foreground font-light leading-relaxed">
                {stat?.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="scroll-fade border-l border-primary/25 pl-6">
          <p className="text-xs text-muted-foreground font-light leading-relaxed max-w-3xl italic">
            Track-record figures represent aggregate activity across relevant fundraising,
            investor-network and ecosystem engagements and should not be interpreted as a guarantee
            of future fundraising or investment outcomes.
          </p>
        </div>
      </div>
    </section>
  );
}
